import { useEffect, useState } from 'react';
import { Search, TrendingUp } from 'lucide-react';
import api from '../lib/api';
import Header from '../components/Layout/Header';
import { Input } from '../components/ui/input';
import { format } from 'date-fns';

const TYPE_COLORS = {
  'Payment Received':          'bg-emerald-50 text-emerald-700',
  'Income - Advance Payment':  'bg-blue-50 text-blue-700',
  'Income - Partial Payment':  'bg-amber-50 text-amber-700',
};

const Payments = () => {
  const [payments, setPayments]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]         = useState('');
  const [filterType, setFilterType] = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');

  useEffect(() => { loadPayments(); }, []);

  const loadPayments = async () => {
    try {
      const res = await api.get('/payments');
      setPayments(res.data);
    } catch (err) {
      console.error('Failed to load payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const types = [...new Set(payments.map(p => p.transaction_type))].filter(Boolean);

  const filtered = payments.filter(p => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      p.client_name?.toLowerCase().includes(q) ||
      p.project_name?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.transaction_type?.toLowerCase().includes(q);
    const matchType = !filterType || p.transaction_type === filterType;
    const pDate = p.date ? p.date.slice(0, 10) : '';
    const matchFrom = !dateFrom || pDate >= dateFrom;
    const matchTo   = !dateTo   || pDate <= dateTo;
    return matchSearch && matchType && matchFrom && matchTo;
  });

  const total = filtered.reduce((s, p) => s + (p.amount || 0), 0);

  const formatDate = (d) => {
    if (!d) return '-';
    try { return format(new Date(d), 'dd MMM yyyy'); } catch { return d; }
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Header title="Payments" subtitle="All client payments received" />

      <div className="p-6 space-y-6">
        {/* Summary card */}
        <div className="bg-white rounded-xl border border-slate-100 p-5 flex items-center gap-4 w-fit shadow-sm">
          <div className="w-11 h-11 bg-emerald-50 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Total Received{search || filterType ? ' (filtered)' : ''}</p>
            <p className="text-2xl font-bold text-slate-900">
              ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-slate-400">{filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search client, project, description…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-white border-slate-200"
            />
          </div>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
          >
            <option value="">All types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="bg-white border-slate-200 w-40"
            />
            <span className="text-slate-400 text-sm">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="bg-white border-slate-200 w-40"
            />
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-xs text-slate-400 hover:text-slate-600 underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading payments…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">No payments found</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Client</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Project</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((p, i) => (
                  <tr key={p.id || i} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap">{formatDate(p.date)}</td>
                    <td className="px-5 py-3.5 font-medium text-slate-800">{p.client_name || '-'}</td>
                    <td className="px-5 py-3.5 text-slate-600">{p.project_name || '-'}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[p.transaction_type] || 'bg-slate-100 text-slate-600'}`}>
                        {p.transaction_type}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-500 max-w-xs truncate">{p.description || '-'}</td>
                    <td className="px-5 py-3.5 text-right font-semibold text-emerald-600 whitespace-nowrap">
                      ₹{(p.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50/60">
                  <td colSpan={5} className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Total</td>
                  <td className="px-5 py-3 text-right font-bold text-slate-900">
                    ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payments;
